import { useState } from 'react';
import {
    TextField,
    Button,
    Typography,
    Grid,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    CardActions,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPlan } from '../../../services/plans';
import SessionBuilder from './SessionBuilder';
import QuestionnaireStep from './QuestionnaireStep';
import LearningContentStep from './LearningContentStep';

const AEROBIC_TYPES = [
    'rehabilitation',
    'treadmill',
    'ergometer',
    'walking',
    'running',
    'cycling',
    'rest',
    'other'
];

const RESISTANCE_TYPES = [
    'squat',
    'sit_to_stand',
    'push',
    'pull',
    'arm_raise',
    'knee_extension'
];

const CreatePlan = ({
    initialName = '',
    initialDescription = '',
    initialData = {
        sessions: [],
        food: [],
        questionnaires: [],
        initialQuestionnaires: [],
        learningContents: []
    }
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = useState(0);
    const [durationWeeks, setDurationWeeks] = useState(4);

    const [name, setName] = useState(initialName);
    const [description, setDescription] = useState(initialDescription);
    const [data, setData] = useState({
        ...initialData,
        durationWeeks: initialData.durationWeeks || 4,
        learningContents: [],
        initialLearningContents: []
    });

    const [errors, setErrors] = useState({});
    const [editingSession, setEditingSession] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editingSessionId, setEditingSessionId] = useState(null);

    const steps = [
        t('plans.steps.details'),
        t('plans.steps.sessions'),
        t('plans.steps.questionnaires'),
        t('plans.steps.learningContents'),
        t('plans.steps.review')
    ];

    const TYPE_LABELS = {
        treadmill: t('exercise.types.treadmill'),
        ergometer: t('exercise.types.ergometer'),
        walking: t('exercise.types.walking'),
        running: t('exercise.types.running'),
        cycling: t('exercise.types.cycling'),
        rest: t('exercise.types.rest'),
        easy: t('exercise.types.easy'),
        other: t('exercise.types.other')
    };

    const RESISTANCE_LABELS = {
        squat: t('exercise.types.squat'),
        sit_to_stand: t('exercise.types.sit_to_stand'),
        push: t('exercise.types.push'),
        pull: t('exercise.types.pull'),
        arm_raise: t('exercise.types.arm_raise'),
        knee_extension: t('exercise.types.knee_extension')
    };

    const renderExercisePrimary = (ex) => {
        if (AEROBIC_TYPES.includes(ex.type)) {
            const label = TYPE_LABELS[ex.type] ?? ex.type;
            const minutes = ex.duration ? Math.round(ex.duration / 60) : 0;

            return `${label} - ${minutes} min`;
        }

        if (RESISTANCE_TYPES.includes(ex.type)) {
            const label = RESISTANCE_LABELS[ex.type] ?? ex.type;
            const sets = ex.sets ?? '-';
            const reps = ex.reps ?? '-';

            return `${label} - ${sets}x${reps}`;
        }

        return ex.type;
    };

    const handleToggleEdit = (id) => {
        setEditingSessionId((prev) => (prev === id ? null : id));
    };

    const handleUpdateSessionInline = (id, updated) => {
        setData((prev) => ({
            ...prev,
            sessions: prev.sessions.map((s) => (s.id === id ? { ...s, ...updated } : s))
        }));
        setEditingSessionId(null);
    };

    const handleAddSession = (session) => {
        setData((prev) => ({
            ...prev,
            sessions: [...prev.sessions, session]
        }));
    };

    const handleDurationChange = (weeks) => {
        setDurationWeeks(weeks);
        setData((prev) => ({
            ...prev,
            durationWeeks: weeks
        }));
    };

    const handleDeleteSession = (id) => {
        setData((prev) => ({
            ...prev,
            sessions: prev.sessions.filter((s) => s.id !== id)
        }));
    };

    const handleUpdateSession = (updatedSession) => {
        setData((prev) => ({
            ...prev,
            sessions: prev.sessions.map((s) =>
                s.id === updatedSession.id ? updatedSession : s
            )
        }));
        setEditingSession(null);
        setIsEditing(false);
    };

    const handleNext = () => {
        if (activeStep === 0) {
            let newErrors = {};
            if (!name) newErrors.name = t('plans.validation.missingName');
            if (!description)
                newErrors.description = t('plans.validation.missingDescription');
            setErrors(newErrors);
            if (Object.keys(newErrors).length > 0) return;
        }

        if (activeStep === 1 && data.sessions.length === 0) {
            alert(t('plans.validation.noSessions'));
            return;
        }

        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleAddQuestionnaire = (questionnaire) => {
        setData((prev) => ({
            ...prev,
            questionnaires: [...prev.questionnaires, questionnaire]
        }));
    };

    const handleRemoveQuestionnaire = (uuid) => {
        setData((prev) => ({
            ...prev,
            questionnaires: prev.questionnaires.filter(
                (q) => q.questionnaireUuid !== uuid
            )
        }));
    };

    const handleAddInitialQuestionnaire = (questionnaire) => {
        setData((prev) => ({
            ...prev,
            initialQuestionnaires: [...prev.initialQuestionnaires, questionnaire]
        }));
    };

    const handleRemoveInitialQuestionnaire = (uuid) => {
        setData((prev) => ({
            ...prev,
            initialQuestionnaires: prev.initialQuestionnaires.filter(
                (q) => q.questionnaireUuid !== uuid
            )
        }));
    };

    const handleUpdateQuestionnaire = (uuid, weeks) => {
        setData((prev) => ({
            ...prev,
            questionnaires: prev.questionnaires.map((q) =>
                q.questionnaireUuid === uuid ? { ...q, assignedWeeks: weeks } : q
            )
        }));
    };

    const handleAddLearningContent = (content) => {
        setData((prev) => ({
            ...prev,
            learningContents: [...prev.learningContents, content]
        }));
    };

    const handleRemoveLearningContent = (uuid) => {
        setData((prev) => ({
            ...prev,
            learningContents: prev.learningContents.filter((c) => c.contentUuid !== uuid)
        }));
    };

    const handleAddInitialLearningContent = (content) => {
        setData((prev) => ({
            ...prev,
            initialLearningContents: [...prev.initialLearningContents, content]
        }));
    };

    const handleRemoveInitialLearningContent = (uuid) => {
        setData((prev) => ({
            ...prev,
            initialLearningContents: prev.initialLearningContents.filter(
                (c) => c.contentUuid !== uuid
            )
        }));
    };

    const handleUpdateLearningContent = (uuid, week) => {
        setData((prev) => ({
            ...prev,
            learningContents: prev.learningContents.map((c) =>
                c.contentUuid === uuid ? { ...c, assignedWeek: week } : c
            )
        }));
    };

    const handleCreate = async () => {
        try {
            await createPlan({ name, description, data });
            alert(t('plans.alerts.success'));
            navigate('/planos');
        } catch (err) {
            console.error(err);
            alert(t('plans.alerts.error'));
        }
    };

    return (
        <Card sx={{ m: 3, p: 3 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                {t('plans.createNew')}
            </Typography>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 && (
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('plans.steps.details')}
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label={t('plans.labels.name')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('plans.labels.durationWeeks')}
                                value={durationWeeks}
                                onChange={(e) =>
                                    handleDurationChange(Number(e.target.value))
                                }
                                inputProps={{ min: 1, max: 52 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label={t('plans.labels.description')}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                error={!!errors.description}
                                helperText={errors.description}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            )}

            {activeStep === 1 && (
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('plans.steps.details')}
                    </Typography>
                    <SessionBuilder
                        onAdd={handleAddSession}
                        onUpdate={handleUpdateSession}
                        editingSession={editingSession}
                    />

                    {data.sessions.length > 0 && (
                        <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {t('plans.createdSessions')}
                            </Typography>
                            <Divider sx={{ my: 1 }} />
                            {data.sessions.map((s) => (
                                <Accordion key={s.id} sx={{ mb: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography fontWeight="bold">
                                                {s.name} ({s.exercises.length}{' '}
                                                {t('plans.exercices')})
                                            </Typography>
                                            <Box>
                                                <IconButton
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleToggleEdit(s.id);
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteSession(s.id);
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </AccordionSummary>

                                    <AccordionDetails>
                                        {editingSessionId === s.id ? (
                                            <Box sx={{ mt: 2 }}>
                                                <SessionBuilder
                                                    existingSession={s}
                                                    onUpdate={(updated) =>
                                                        handleUpdateSessionInline(
                                                            s.id,
                                                            updated
                                                        )
                                                    }
                                                    onCancelEdit={() =>
                                                        setEditingSessionId(null)
                                                    }
                                                    inlineMode
                                                />
                                            </Box>
                                        ) : (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {s.description ||
                                                        t('plans.noDescription')}
                                                </Typography>
                                                <List dense>
                                                    {s.exercises.map((ex) => (
                                                        <ListItem key={ex.id}>
                                                            <ListItemText
                                                                primary={`${ex.order}. ${renderExercisePrimary(ex)}`}
                                                                secondary={ex.description}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Paper>
                    )}
                </CardContent>
            )}

            {activeStep === 2 && (
                <CardContent>
                    <QuestionnaireStep
                        selectedQuestionnaires={data.questionnaires}
                        initialQuestionnaires={data.initialQuestionnaires}
                        durationWeeks={data.durationWeeks}
                        onAdd={handleAddQuestionnaire}
                        onRemove={handleRemoveQuestionnaire}
                        onUpdate={handleUpdateQuestionnaire}
                        onAddInitial={handleAddInitialQuestionnaire}
                        onRemoveInitial={handleRemoveInitialQuestionnaire}
                    />
                </CardContent>
            )}

            {activeStep === 3 && (
                <CardContent>
                    <LearningContentStep
                        selectedContents={data.learningContents}
                        initialContents={data.initialLearningContents}
                        durationWeeks={data.durationWeeks}
                        onAdd={handleAddLearningContent}
                        onRemove={handleRemoveLearningContent}
                        onUpdate={handleUpdateLearningContent}
                        onAddInitial={handleAddInitialLearningContent}
                        onRemoveInitial={handleRemoveInitialLearningContent}
                    />
                </CardContent>
            )}

            {activeStep === 4 && (
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t('plans.reviewPlan')}
                    </Typography>

                    <Typography variant="subtitle1">
                        <strong>{t('plans.labels.name')}:</strong> {name}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                        <strong>{t('plans.labels.durationWeeks')}:</strong>{' '}
                        {durationWeeks}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                        <strong>{t('plans.labels.description')}:</strong> {description}
                    </Typography>

                    <Divider sx={{ my: 2 }} />
                    {data.sessions.map((s) => (
                        <Paper key={s.id} sx={{ p: 2, mb: 2 }} variant="outlined">
                            <Typography variant="subtitle1" fontWeight="bold">
                                {s.name}
                            </Typography>
                            <Typography variant="body2">{s.description}</Typography>
                            <List dense>
                                {s.exercises.map((ex) => (
                                    <ListItem key={ex.id}>
                                        <ListItemText
                                            primary={`${ex.order}. ${renderExercisePrimary(ex)}`}
                                            secondary={ex.description}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    {/* Questionários */}
                    <Typography variant="h4">
                        {t('plans.questionnaires.title')}
                    </Typography>
                    <Typography variant="h6">
                        {t('plans.questionnaires.initial')}
                    </Typography>

                    {data.initialQuestionnaires.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('plans.questionnaires.none')}
                        </Typography>
                    ) : (
                        data.initialQuestionnaires.map((q) => (
                            <Typography key={q.questionnaireUuid} variant="body2">
                                • {q.questionnaireName}
                            </Typography>
                        ))
                    )}

                    <Typography variant="h6">
                        {t('plans.questionnaires.scheduledQuestionaires')}
                    </Typography>

                    {data.questionnaires.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('plans.questionnaires.none')}
                        </Typography>
                    ) : (
                        data.questionnaires.map((q) => (
                            <Typography key={q.questionnaireUuid} variant="body2">
                                • {q.questionnaireName}{' '}
                                <strong>
                                    ({t('plans.questionnaires.week')}:{' '}
                                    {Array.isArray(q.assignedWeeks) &&
                                    q.assignedWeeks.length > 0
                                        ? q.assignedWeeks.join(', ')
                                        : t('plans.notAssigned')}
                                    )
                                </strong>
                            </Typography>
                        ))
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Conteúdos iniciais */}
                    <Typography variant="h4">
                        {t('plans.learningContents.title')}
                    </Typography>
                    <Typography variant="h6">
                        {t('plans.learningContents.initial')}
                    </Typography>

                    {data.initialLearningContents?.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('plans.learningContents.none')}
                        </Typography>
                    ) : (
                        data.initialLearningContents.map((c) => (
                            <Typography key={c.contentUuid} variant="body2">
                                • {c.contentTitle}
                            </Typography>
                        ))
                    )}

                    {/* Conteúdos agendados */}
                    <Typography variant="h6">
                        {t('plans.learningContents.selected')}
                    </Typography>

                    {data.learningContents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            {t('plans.learningContents.none')}
                        </Typography>
                    ) : (
                        data.learningContents.map((c) => (
                            <Typography key={c.contentUuid} variant="body2">
                                • {c.contentTitle}{' '}
                                <strong>
                                    ({t('plans.questionnaires.week')}{' '}
                                    {c.assignedWeek ?? t('plans.notAssigned')})
                                </strong>
                            </Typography>
                        ))
                    )}
                </CardContent>
            )}

            <CardActions sx={{ justifyContent: 'space-between', mt: 2 }}>
                {activeStep > 0 ? (
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBack}
                    >
                        {t('plans.buttons.back')}
                    </Button>
                ) : (
                    <Button variant="outlined" onClick={() => navigate('/planos')}>
                        {t('plans.buttons.cancel')}
                    </Button>
                )}

                {activeStep < 4 ? (
                    <Button
                        variant="contained"
                        endIcon={<ArrowForwardIosIcon />}
                        onClick={handleNext}
                    >
                        {t('plans.buttons.next')}
                    </Button>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleCreate}>
                        {t('plans.buttons.create')}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

export default CreatePlan;
